import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/objectWithoutPropertiesLoose';
import PropTypes from 'prop-types';
import React from 'react';
import Illustration from './Illustration.js';
import Avatar from './Avatar.js';
import Icon from './Icon.js';
import { a as addSpacingClasses, p as paddingProps } from './spacing-d13ff368.js';
import { p as props, d as defaults } from './componentEl-33ff2aeb.js';
import { a as addClassNames, c as classNameProps } from './classNames-6a4763fd.js';
import './_variables.es6-3122d3a2.js';

/*
Component to display a single notification
*/

function NotificationsItem(_ref) {
  let {
    componentEl: Component,
    avatar,
    icon,
    timestamp,
    initials,
    children,
    nohover,
    highlight,
    padding
  } = _ref,
      otherProps = _objectWithoutPropertiesLoose(_ref, ["componentEl", "avatar", "icon", "timestamp", "initials", "children", "nohover", "highlight", "padding"]);

  let cssClasses = ['st_notification'];
  cssClasses = addSpacingClasses({
    padding,
    cssClasses
  });
  if (highlight) cssClasses.push('st_notification--highlight');
  if (nohover) cssClasses.push('st_notification--nohover');
  if (!avatar && !initials) cssClasses.push('st_notification--noavatar');
  if (!icon) cssClasses.push('st_notification--noicon');
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), (avatar || initials) && /*#__PURE__*/React.createElement("div", {
    className: "st_notification__avatar"
  }, /*#__PURE__*/React.createElement(Avatar, {
    avatar: avatar,
    initials: initials,
    margin: {
      top: 'sm'
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: "st_notification__text"
  }, children), timestamp && /*#__PURE__*/React.createElement("time", {
    className: "st_notification__time"
  }, timestamp), icon && /*#__PURE__*/React.createElement(Icon, {
    type: icon === true ? 'caret-right' : icon,
    className: "st_notification__icon"
  }));
} // proptypes definitions


NotificationsItem.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,

  /** url to avatar, if available */
  avatar: PropTypes.string,

  /** timestamp for event */
  timestamp: PropTypes.string,

  /** whether component should look highlighted */
  highlight: PropTypes.bool,

  /** fallback initials for avatar */
  initials: PropTypes.string,

  /** optional icon to show on right, defaults to caret if no icon type specified */
  icon: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),

  /** whether to suppress hover for this item */
  nohover: PropTypes.bool,

  /** padding for item. Setting this overrides defaults and does not extend them */
  padding: paddingProps
}; // default props

NotificationsItem.defaultProps = {
  componentEl: defaults,
  avatar: null,
  highlight: false,
  timestamp: null,
  initials: null,
  icon: false,
  nohover: false,
  padding: {
    vertical: 'sm',
    horizontal: 'mlg'
  }
};
NotificationsItem.displayName = 'Notifications.Item';

/*
Component to display list of notifications
and empty message if there are no notifications to display
*/

function Notifications(_ref) {
  let {
    children,
    emptyTitle,
    emptyMessage,
    size,
    className
  } = _ref,
      otherProps = _objectWithoutPropertiesLoose(_ref, ["children", "emptyTitle", "emptyMessage", "size", "className"]);

  let cssClasses = [];

  if (React.Children.count(children) === 0) {
    cssClasses = ['st_notifications__empty', "st_notifications__empty--" + size];
    cssClasses = addClassNames(className, cssClasses);
    return /*#__PURE__*/React.createElement("div", _extends({
      className: cssClasses.join(' ')
    }, otherProps), /*#__PURE__*/React.createElement(Illustration, {
      name: "no-notifications",
      className: "st_notifications__emptyimg"
    }), emptyTitle && /*#__PURE__*/React.createElement("h3", {
      className: "st_notifications__emptytitle"
    }, emptyTitle), emptyMessage && /*#__PURE__*/React.createElement("p", {
      className: "st_notifications__emptytext"
    }, emptyMessage));
  }

  cssClasses = addClassNames(className, ['st_notifications']);
  return /*#__PURE__*/React.createElement("div", _extends({
    className: cssClasses.join(' ')
  }, otherProps), children);
} // proptypes definitions


Notifications.propTypes = {
  /** message to display if there are no notifications */
  emptyMessage: PropTypes.string,

  /** title to display if there are no notifications */
  emptyTitle: PropTypes.string,

  /** size of notifications */
  size: PropTypes.oneOf(['md', 'lg']),

  /** any extra CSS classes */
  className: classNameProps
}; // default props

Notifications.defaultProps = {
  emptyMessage: null,
  emptyTitle: null,
  size: 'md',
  className: null
};
Notifications.Item = NotificationsItem;

export default Notifications;
